import * as React from "react";
import { Loader2, Trash2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useDeleteContainer } from "@/hooks/useContainers";
import { ApiError } from "@/lib/api";

interface DeleteDomainButtonProps {
  containerId: number;
  domainName: string;
}

/**
 * Trash action on a plan's domain row. Hard-confirms before firing the
 * real-time removal (20i hosting detached/deleted, then the domain here).
 * Frees the plan slot on success.
 */
export function DeleteDomainButton({ containerId, domainName }: DeleteDomainButtonProps) {
  const del = useDeleteContainer();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);

  async function onConfirm() {
    try {
      await del.mutateAsync(containerId);
      setOpen(false);
      toast({
        title: "Domain removed",
        description: `${domainName} and its hosting are gone. The plan slot is free again.`,
        variant: "success"
      });
    } catch (err) {
      toast({
        title: "Couldn't remove domain",
        description: err instanceof ApiError ? err.message : "Network error — try again.",
        variant: "destructive"
      });
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          type="button"
          className="shrink-0 rounded-md p-2 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          aria-label={`Remove ${domainName} from this plan`}
        >
          {del.isPending
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Trash2 className="h-4 w-4" />}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {domainName}?</AlertDialogTitle>
          <AlertDialogDescription>
            This immediately removes the domain's hosting from 20i — every
            mailbox, email message, website file and DNS record on{" "}
            {domainName} is permanently deleted. The domain itself stays
            registered to you, and its plan slot becomes free for another
            domain. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={del.isPending}>Keep domain</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); void onConfirm(); }}
            disabled={del.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {del.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Remove domain
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
